import { ProcessJobStatus, ProcessJobType, type AnalysisRunsQuery } from '@/graphql/graphql';

import { mergeRuns } from './mergeRuns';
import { ANALYSES } from './registry';
import type { AnalysisId } from './types';

export type AnalysisRunItem = AnalysisRunsQuery['analysisRuns'][number];

/** Server job types the hub lists — derived from the registry (D8). LAYER_STACK
 *  is absent because the registry no longer has a layer-stack entry (spec
 *  amendment A1), so the hub neither requests nor lists its runs. Existing stack result layers keep working. */
export const ANALYSIS_RUN_JOB_TYPES: readonly ProcessJobType[] = Array.from(
  new Set(ANALYSES.flatMap((a) => a.jobTypes)),
);

export function analysisIdForJobType(type: ProcessJobType | string): AnalysisId | null {
  return ANALYSES.find((a) => (a.jobTypes as readonly string[]).includes(type))?.id ?? null;
}

/** A run still WAITING/PROCESSING after this long is a stuck batch, not a
 *  reason to keep a 2 s poll alive (workers can fail without failing their
 *  batch). */
export const ACTIVE_RUN_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function isRunActive(run: Pick<AnalysisRunItem, 'status' | 'createdAt'>, nowMs: number): boolean {
  if (run.status !== ProcessJobStatus.Waiting && run.status !== ProcessJobStatus.Processing) return false;
  const created = new Date(run.createdAt as unknown as string).getTime();
  return Number.isFinite(created) && nowMs - created < ACTIVE_RUN_MAX_AGE_MS;
}

/** "N running" — the header badge and the dropdown footer both count this.
 *  Counted per hub row: a paired comparison is one (`mergeRuns`). */
export const countActiveRuns = (runs: readonly AnalysisRunItem[] | undefined, nowMs: number): number =>
  mergeRuns(runs ?? []).filter((r) => isRunActive(r, nowMs)).length;

export { isOptionalStepType, NON_FATAL_STEP_TYPES } from './optionalSteps';
